import {
	TICKET_ATTACHMENT_ALLOWED_MIME_TYPES,
	TICKET_ATTACHMENT_MAX_BYTES,
	TICKET_ATTACHMENT_MAX_FILES,
} from "@signalist/shared-types";

/** The limit as the copy states it ("5 MB each"). */
export const TICKET_ATTACHMENT_MAX_MB = TICKET_ATTACHMENT_MAX_BYTES / (1024 * 1024);

/** Which i18n error to show, with the values it interpolates. */
export interface AttachmentRejection {
	key: "tooLarge" | "tooMany" | "wrongType";
	params: Record<string, string | number>;
}

export interface AttachmentSelection {
	files: File[];
	rejection: AttachmentRejection | null;
}

const isAllowedType = (file: File): boolean =>
	(TICKET_ATTACHMENT_ALLOWED_MIME_TYPES as readonly string[]).includes(file.type);

/**
 * Merge newly picked files into the current selection, applying the same limits the API enforces
 * (PREC-3935) **before** anything is uploaded — an oversize file must not cost the customer a
 * request that can only answer 400. Files picked before the offending one are kept, so the
 * customer does not lose a valid selection to one bad file.
 */
export const selectTicketAttachments = (current: File[], incoming: File[]): AttachmentSelection => {
	const files = [...current];

	for (const file of incoming) {
		if (!isAllowedType(file)) {
			return { files, rejection: { key: "wrongType", params: { name: file.name } } };
		}

		if (file.size > TICKET_ATTACHMENT_MAX_BYTES) {
			return {
				files,
				rejection: {
					key: "tooLarge",
					params: { name: file.name, maxMb: TICKET_ATTACHMENT_MAX_MB },
				},
			};
		}

		if (files.length >= TICKET_ATTACHMENT_MAX_FILES) {
			return {
				files,
				rejection: { key: "tooMany", params: { maxFiles: TICKET_ATTACHMENT_MAX_FILES } },
			};
		}

		files.push(file);
	}

	return { files, rejection: null };
};

/** Human-readable size for an attachment chip. */
export const formatFileSize = (bytes: number): string =>
	bytes >= 1024 * 1024
		? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
		: `${Math.max(1, Math.round(bytes / 1024))} KB`;
